import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FiInstagram, FiYoutube, FiTwitter, FiFacebook, FiLinkedin, FiLink2
} from 'react-icons/fi';
import { SiTiktok, SiThreads, SiTelegram, SiTwitch, SiPinterest, SiWhatsapp } from 'react-icons/si';

type CreatorDetails = {
  id: string;
  full_name?: string | null;
  brand_name?: string | null;
  profile_photo_url?: string | null;
  country?: string | null;
  city?: string | null;
  primary_niche?: string | null;
  secondary_niches?: string | null;
  short_bio?: string | null;
  primary_website?: string | null;
  best_contact_email?: string | null;
  total_followers?: number | null;
  avg_engagement_rate?: number | null;
  content_type?: string | null;
  influencer_type?: string | null;
  total_views?: string | null;
  languages?: string | null;
  gender?: string | null;
  discovery_source?: string | null;
  data_quality_score?: number | null;
};

type SocialProfile = {
  id: string;
  platform: string;
  handle: string;
  profile_url: string;
  followers_count?: number | null;
  engagement_rate?: number | null;
  is_verified?: boolean;
  social_photo_url?: string | null;
};

type Website = {
  id: string;
  url: string;
  domain?: string | null;
  title?: string | null;
  platform?: string | null;
  contact_page_url?: string | null;
  media_kit_page_url?: string | null;
};

type Email = {
  id: string;
  email: string;
  type?: string | null;
  priority_score?: number | null;
  confidence_score?: number | null;
  source?: string | null;
};

type ApiResponse = {
  success: boolean;
  creator: CreatorDetails;
  socialProfiles: SocialProfile[];
  websites: Website[];
  emails: Email[];
};

const getPlatformIcon = (platform: string) => {
  const platformLower = platform.toLowerCase();
  const iconProps = { size: 28, className: 'flex-shrink-0' };
  
  switch (platformLower) {
    case 'instagram':
      return <FiInstagram {...iconProps} />;
    case 'tiktok':
      return <SiTiktok {...iconProps} />;
    case 'youtube':
      return <FiYoutube {...iconProps} />;
    case 'twitter':
    case 'x':
      return <FiTwitter {...iconProps} />;
    case 'facebook':
      return <FiFacebook {...iconProps} />;
    case 'linkedin':
      return <FiLinkedin {...iconProps} />;
    case 'pinterest':
      return <SiPinterest {...iconProps} />;
    case 'twitch':
      return <SiTwitch {...iconProps} />;
    case 'telegram':
      return <SiTelegram {...iconProps} />;
    case 'whatsapp':
      return <SiWhatsapp {...iconProps} />;
    case 'threads':
      return <SiThreads {...iconProps} />;
    default:
      return <FiLink2 {...iconProps} />;
  }
};

const countryFlags: { [key: string]: string } = {
  'US': '🇺🇸', 'GB': '🇬🇧', 'PL': '🇵🇱',
  'FR': '🇫🇷', 'DE': '🇩🇪', 'IT': '🇮🇹', 'ES': '🇪🇸',
  'JP': '🇯🇵', 'CN': '🇨🇳', 'IN': '🇮🇳', 'BR': '🇧🇷',
  'CA': '🇨🇦', 'AU': '🇦🇺', 'MX': '🇲🇽', 'KR': '🇰🇷',
};

export default function CreatorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [creator, setCreator] = useState<CreatorDetails | null>(null);
  const [socialProfiles, setSocialProfiles] = useState<SocialProfile[]>([]);
  const [websites, setWebsites] = useState<Website[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCreator = async () => {
      if (!id) {
        setError('Creator ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`/api/creators/${id}`);
        if (!response.ok) {
          throw new Error('Failed to load creator details');
        }
        const data: ApiResponse = await response.json();
        setCreator(data.creator);
        setSocialProfiles(data.socialProfiles || []);
        setWebsites(data.websites || []);
        setEmails(data.emails || []);
      } catch (err: any) {
        console.error('Error loading creator:', err);
        setError(err.message || 'Failed to load creator');
      } finally {
        setLoading(false);
      }
    };

    loadCreator();
  }, [id]);

  if (loading) {
    return (
      <div className="flex-1 bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-64 bg-gray-200 rounded" />
            <div className="h-8 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !creator) {
    return (
      <div className="flex-1 bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-red-600 text-lg mb-4">{error || 'Creator not found'}</p>
          <button
            onClick={() => navigate('/recommended/top-influencers')}
            className="bg-black text-waybook-green px-6 py-2 font-semibold hover:bg-gray-900 transition-colors"
          >
            Back to Influencers
          </button>
        </div>
      </div>
    );
  }

  const name = creator.full_name || creator.brand_name || 'Unknown Creator';
  const countryCode = creator.country?.substring(0, 2).toUpperCase() || '';
  const flag = countryFlags[countryCode] || '';
  const languageList = creator.languages
    ? creator.languages.split(',').map(l => l.trim())
    : [];

  const seoTitle = `${name} - Travel Creator Profile | Waybook`;
  const seoDescription = creator.short_bio 
    ? `${creator.short_bio.substring(0, 155)}...`
    : `Discover ${name}, a travel creator from ${creator.city || creator.country || 'around the world'}. ${creator.total_followers ? `${creator.total_followers.toLocaleString()} followers` : 'Explore their journey'}.`;

  return (
    <div className="flex-1 bg-white py-12">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={`${name}, travel creator, ${creator.primary_niche || 'travel'}, ${creator.country || ''}, travel influencer, ${creator.content_type || ''}`}
        ogImage={creator.profile_photo_url || '/og-image.png'}
        canonicalUrl={`https://www.waybook.me/creator/${id}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: name,
          description: creator.short_bio || `Travel creator specializing in ${creator.primary_niche || 'travel'}`,
          image: creator.profile_photo_url || undefined,
          url: `https://www.waybook.me/creator/${id}`,
          jobTitle: 'Travel Creator',
          ...(creator.country && { nationality: creator.country }),
          ...(creator.primary_website && { url: creator.primary_website }),
          ...(socialProfiles.length > 0 && {
            sameAs: socialProfiles.map(sp => sp.profile_url).filter(Boolean)
          })
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/recommended/top-influencers')}
          className="mb-8 text-sm text-gray-600 hover:text-black underline"
        >
          ← Back to Influencers
        </button>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Photo and Main Contact */}
          <div className="lg:col-span-1">
            {/* Photo */}
            {creator.profile_photo_url ? (
              <img
                src={creator.profile_photo_url}
                alt={name}
                className="w-full aspect-square object-cover rounded mb-6"
              />
            ) : (
              <div className="w-full aspect-square bg-gray-200 rounded flex items-center justify-center mb-6">
                <span className="text-8xl font-bold text-gray-300">{name.charAt(0)}</span>
              </div>
            )}

            {/* Quick Stats */}
            <div className="space-y-4">
              {creator.total_followers && creator.total_followers > 0 && (
                <div className="border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Total Followers</p>
                  <p className="text-2xl font-bold">
                    {creator.total_followers.toLocaleString()}
                  </p>
                </div>
              )}

              {creator.avg_engagement_rate !== null && creator.avg_engagement_rate !== undefined && (
                <div className="border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Engagement</p>
                  <p className="text-2xl font-bold">
                    {(creator.avg_engagement_rate * 100).toFixed(1)}%
                  </p>
                </div>
              )}

              {creator.total_views && (
                <div className="border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Total Views</p>
                  <p className="text-lg font-semibold">{creator.total_views}</p>
                </div>
              )}

              {creator.data_quality_score && (
                <div className="border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Data Quality</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-full bg-black rounded-full"
                        style={{ width: `${Math.min(creator.data_quality_score, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold">{creator.data_quality_score.toFixed(0)}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Content Area */}
          <div className="lg:col-span-3 space-y-8">
            {/* Name and Location */}
            <div>
              <h1 className="text-4xl font-bold mb-3">{name}</h1>
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                {flag && <span className="text-2xl">{flag}</span>}
                <span className="text-lg">
                  {creator.city && creator.country
                    ? `${creator.city}, ${creator.country}`
                    : creator.country || 'Unknown Location'}
                </span>
              </div>
            </div>

            {/* Bio */}
            {creator.short_bio && (
              <div>
                <h2 className="text-lg font-semibold mb-3">About</h2>
                <p className="text-gray-700 leading-relaxed">{creator.short_bio}</p>
              </div>
            )}

            {/* Niche */}
            {creator.primary_niche && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Primary Niche</h2>
                <p className="text-gray-700 leading-relaxed">{creator.primary_niche}</p>
              </div>
            )}

            {/* Additional Details */}
            {(creator.content_type || creator.secondary_niches) && (
              <div className="grid grid-cols-2 gap-6">
                {creator.content_type && (
                  <div>
                    <p className="text-sm text-gray-500 uppercase mb-2">Content Type</p>
                    <p className="text-base font-semibold">{creator.content_type}</p>
                  </div>
                )}

                {creator.secondary_niches && (
                  <div>
                    <p className="text-sm text-gray-500 uppercase mb-2">Secondary Niches</p>
                    <p className="text-base font-semibold">{creator.secondary_niches}</p>
                  </div>
                )}
              </div>
            )}

            {/* Social Profiles */}
            {socialProfiles.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Social Media Profiles</h2>
                <div className="space-y-3">
                  {socialProfiles.map((profile) => {
                    return (
                      <a
                        key={profile.id}
                        href={profile.profile_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 p-4 border border-gray-200 hover:border-black transition-colors group"
                      >
                        <div className="text-black group-hover:text-gray-600">
                          {getPlatformIcon(profile.platform)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">@{profile.handle}</p>
                        </div>
                        <div className="text-right">
                          {profile.followers_count && (
                            <p className="text-sm font-semibold">{profile.followers_count.toLocaleString()} followers</p>
                          )}
                          {profile.engagement_rate && (
                            <p className="text-xs text-gray-600">{(profile.engagement_rate * 100).toFixed(1)}% engagement</p>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Websites */}
            {websites.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Websites</h2>
                <div className="space-y-3">
                  {websites.map((website) => (
                    <div key={website.id} className="border border-gray-200 p-4">
                      <a
                        href={website.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-blue-600 hover:text-blue-800 break-all"
                      >
                        {website.title || website.domain || website.url}
                      </a>
                      {website.platform && (
                        <p className="text-sm text-gray-600 mt-1">Platform: {website.platform}</p>
                      )}
                      <div className="flex gap-4 mt-2">
                        {website.contact_page_url && (
                          <a
                            href={website.contact_page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-600 hover:text-black underline"
                          >
                            Contact Page
                          </a>
                        )}
                        {website.media_kit_page_url && (
                          <a
                            href={website.media_kit_page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-600 hover:text-black underline"
                          >
                            Media Kit
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email Contacts */}
            {emails.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Contact Emails</h2>
                <div className="space-y-3">
                  {emails.map((email) => (
                    <div key={email.id} className="border border-gray-200 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <a
                            href={`mailto:${email.email}`}
                            className="font-semibold text-blue-600 hover:text-blue-800 break-all"
                          >
                            {email.email}
                          </a>
                          <div className="flex gap-3 mt-2 text-xs">
                            {email.type && (
                              <span className="bg-gray-100 px-2 py-1 rounded capitalize">
                                {email.type}
                              </span>
                            )}
                            {email.source && (
                              <span className="bg-gray-100 px-2 py-1 rounded capitalize">
                                {email.source}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {email.priority_score && (
                            <p className="text-sm font-semibold">Priority: {email.priority_score}/10</p>
                          )}
                          {email.confidence_score && (
                            <p className="text-xs text-gray-600">{(email.confidence_score * 100).toFixed(0)}% confidence</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Primary Website CTA */}
            {creator.primary_website && (
              <div className="pt-4 border-t border-gray-200">
                <a
                  href={creator.primary_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-black text-waybook-green px-6 py-3 font-semibold hover:bg-gray-900 transition-colors"
                >
                  Visit Primary Website
                  <span>→</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
